import React, { useEffect, useMemo, useRef, useState } from "react";
import { Send, Paperclip, X, Edit2, Trash2, Image as ImageIcon, FileText, Loader2, AtSign, Check, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLive } from "../lib/live";

// --- helpers ---

// Map an entityKey to its REST URL base. Used by inner components that don't
// receive `urlBase` directly via props.
function urlBaseForEntityKey(entityKey) {
  if (entityKey === "internal_ticket") return "/internal-tickets";
  if (entityKey === "ticket") return "/tickets";
  if (entityKey === "chelly_client") return "/chelly/clients";
  return "/dossiers";
}

function timeAgo(iso) {
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 30) return "à l'instant";
  if (sec < 60) return `il y a ${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const day = Math.floor(h / 24);
  if (day < 7) return `il y a ${day} j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/[\s.]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Stable color per author based on hash
function avatarColor(email) {
  const palette = [
    "bg-emerald-100 text-emerald-800",
    "bg-sky-100 text-sky-800",
    "bg-amber-100 text-amber-800",
    "bg-fuchsia-100 text-fuchsia-800",
    "bg-rose-100 text-rose-800",
    "bg-indigo-100 text-indigo-800",
    "bg-teal-100 text-teal-800",
  ];
  let h = 0;
  for (let i = 0; i < (email || "").length; i++) h = (h * 31 + email.charCodeAt(i)) & 0xfffffff;
  return palette[h % palette.length];
}

// Highlight @handle in body
function renderBody(body, mentions, currentHandle) {
  const handlesMap = new Map((mentions || []).map((m) => [m.handle, m]));
  const parts = body.split(/(@[a-zA-Z0-9][\w.\-]{1,40})/g);
  return parts.map((p, i) => {
    if (!p.startsWith("@")) return <React.Fragment key={i}>{p}</React.Fragment>;
    const handle = p.slice(1).toLowerCase();
    const m = handlesMap.get(handle);
    if (!m) return <React.Fragment key={i}>{p}</React.Fragment>;
    const isMe = currentHandle && handle === currentHandle;
    return (
      <span
        key={i}
        className={`inline-block px-1 rounded font-medium ${
          isMe ? "bg-norea-accent/25 text-norea-accent" : "bg-norea-primary/10 text-norea-primary"
        }`}
        title={m.email}
      >
        @{handle}
      </span>
    );
  });
}

export default function DossierDiscussion({ dossierId, entityType = "dossier" }) {
  // entityType: "dossier" (default), "ticket", "internal_ticket" or "chelly_client" — drives the API URL path.
  const entityId = dossierId;
  const entityKey =
    entityType === "ticket" ? "ticket"
    : entityType === "internal_ticket" ? "internal_ticket"
    : entityType === "chelly_client" ? "chelly_client"
    : "dossier";
  // The URL base differs from the entityKey for internal tickets (kebab-case)
  const urlBase = urlBaseForEntityKey(entityKey);
  const eventPrefix = entityKey; // "dossier:message:..." or "ticket:message:..."
  const payloadIdField = `${entityKey}_id`;
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);

  // Compute "my handle" from candidates (or fallback)
  const myCandidate = candidates.find((c) => c.email === me?.email);
  const myHandle = myCandidate?.handle || (me?.email || "").split("@")[0].toLowerCase();
  const myName = myCandidate?.name || me?.email || "Vous";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${urlBase}/${entityId}/messages`);
      setMessages(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // mention-candidates endpoint lives on /dossiers/_meta/ but returns all users — reuse it.
    api.get("/dossiers/_meta/mention-candidates").then(({ data }) => setCandidates(data.items || []));
  }, [entityId]);

  // Live updates
  const { subscribe } = useLive();
  useEffect(
    () =>
      subscribe((ev) => {
        if (!ev?.payload || ev.payload[payloadIdField] !== entityId) return;
        if (ev.type === `${eventPrefix}:message:created`) {
          setMessages((prev) => (prev.some((m) => m.id === ev.payload.message.id) ? prev : [...prev, ev.payload.message]));
        } else if (ev.type === `${eventPrefix}:message:updated`) {
          setMessages((prev) => prev.map((m) => (m.id === ev.payload.message.id ? ev.payload.message : m)));
        } else if (ev.type === `${eventPrefix}:message:deleted`) {
          setMessages((prev) => prev.filter((m) => m.id !== ev.payload.message_id));
        }
      }),
    [entityId, subscribe, eventPrefix, payloadIdField]
  );

  const handleDelete = async (id) => {
    try {
      await api.delete(`${urlBase}/${entityId}/messages/${id}`);
      toast.success("Message supprimé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const handleEdit = async (id, newBody) => {
    try {
      await api.patch(`${urlBase}/${entityId}/messages/${id}`, { body: newBody });
      toast.success("Message modifié");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="card-flat p-5" data-testid="dossier-discussion">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Discussion ({messages.length})</div>
        <div className="text-[10px] text-norea-textMuted italic">Tagguez un collègue avec @prenom.nom</div>
      </div>

      <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1 mb-4" data-testid="discussion-list">
        {loading && <div className="h-16 rounded bg-norea-bg animate-pulse" />}
        {!loading && messages.length === 0 && (
          <div className="text-center text-sm text-norea-textMuted italic py-6 border border-dashed border-norea-border rounded">
            Aucun message pour le moment. Démarrez la discussion ci-dessous.
          </div>
        )}
        {messages.map((m) => (
          <MessageItem
            key={m.id}
            msg={m}
            currentEmail={me?.email}
            currentHandle={myHandle}
            isAdmin={isAdmin}
            entityKey={entityKey}
            entityId={entityId}
            onDelete={() => handleDelete(m.id)}
            onEdit={(body) => handleEdit(m.id, body)}
          />
        ))}
      </div>

      <ComposeBox
        entityKey={entityKey}
        entityId={entityId}
        candidates={candidates}
        myEmail={me?.email}
        myName={myName}
        onSent={(msg) => {
          // Optimistic — SSE will dedupe by id
          setMessages((prev) => (prev.some((x) => x.id === msg.id) ? prev : [...prev, msg]));
        }}
      />
    </div>
  );
}

function MessageItem({ msg, currentEmail, currentHandle, isAdmin, entityKey, entityId, onDelete, onEdit }) {
  const mine = msg.author_email === currentEmail;
  const canDelete = mine || isAdmin;
  // Edit only own + within window
  const editWindowMs = new Date(msg.edit_window_until).getTime() - Date.now();
  const canEdit = mine && editWindowMs > 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.body);

  useEffect(() => setDraft(msg.body), [msg.body]);

  const att = msg.attachment;
  const isImg = att && (att.content_type || "").startsWith("image/");

  return (
    <div className="flex gap-3 group" data-testid={`message-${msg.id}`}>
      <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold ${avatarColor(msg.author_email)}`}>
        {initials(msg.author_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-0.5">
          <span className="font-medium text-sm text-norea-text">{msg.author_name}</span>
          <span className="text-[10px] text-norea-textMuted" title={msg.created_at}>{timeAgo(msg.created_at)}</span>
          {msg.edited_at && <span className="text-[10px] text-norea-textMuted italic">(modifié)</span>}
        </div>

        {editing ? (
          <EditInline
            initial={draft}
            onCancel={() => { setDraft(msg.body); setEditing(false); }}
            onSave={async (body) => {
              await onEdit(body);
              setEditing(false);
            }}
          />
        ) : (
          <div className="text-sm text-norea-text whitespace-pre-wrap break-words leading-relaxed" data-testid={`message-body-${msg.id}`}>
            {renderBody(msg.body, msg.mentions, currentHandle)}
          </div>
        )}

        {att && (
          <div className="mt-2">
            {isImg ? (
              <ImageAttachment entityKey={entityKey} entityId={entityId} msgId={msg.id} att={att} />
            ) : (
              <FileAttachmentLink entityKey={entityKey} entityId={entityId} msgId={msg.id} att={att} />
            )}
          </div>
        )}

        {!editing && (canEdit || canDelete) && (
          <div className="mt-1.5 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
            {canEdit && (
              <button
                onClick={() => setEditing(true)}
                className="text-[10px] inline-flex items-center gap-1 text-norea-textMuted hover:text-norea-primary"
                data-testid={`message-${msg.id}-edit`}
              >
                <Edit2 className="w-3 h-3" />
                Modifier
              </button>
            )}
            {canDelete && (
              <button
                onClick={onDelete}
                className="text-[10px] inline-flex items-center gap-1 text-norea-textMuted hover:text-status-erreurText"
                data-testid={`message-${msg.id}-delete`}
              >
                <Trash2 className="w-3 h-3" />
                Supprimer
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EditInline({ initial, onSave, onCancel }) {
  const [val, setVal] = useState(initial);
  const [busy, setBusy] = useState(false);
  return (
    <div className="mt-1">
      <textarea
        value={val}
        onChange={(e) => setVal(e.target.value)}
        rows={2}
        className="input-field text-sm w-full"
        autoFocus
        data-testid="edit-textarea"
      />
      <div className="flex gap-2 mt-1.5">
        <button
          disabled={busy || !val.trim()}
          onClick={async () => { setBusy(true); await onSave(val.trim()); setBusy(false); }}
          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-norea-primary text-white disabled:opacity-50"
          data-testid="btn-save-edit"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Enregistrer
        </button>
        <button
          onClick={onCancel}
          className="text-[11px] px-2 py-1 rounded border border-norea-border text-norea-textMuted hover:bg-norea-surfaceHover"
          data-testid="btn-cancel-edit"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

function ImageAttachment({ entityKey, entityId, msgId, att }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke = null;
    const token = localStorage.getItem("norea_token");
    fetch(`${API}${urlBaseForEntityKey(entityKey)}/${entityId}/messages/${msgId}/attachment`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        revoke = url;
        setSrc(url);
      });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [entityKey, entityId, msgId]);
  if (!src) return <div className="h-24 w-40 bg-norea-bg rounded animate-pulse" />;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img src={src} alt={att.filename} className="max-w-xs max-h-48 rounded border border-norea-border hover:border-norea-primary/40 transition" />
    </a>
  );
}

function FileAttachmentLink({ entityKey, entityId, msgId, att }) {
  const open = async () => {
    const token = localStorage.getItem("norea_token");
    const r = await fetch(`${API}${urlBaseForEntityKey(entityKey)}/${entityId}/messages/${msgId}/attachment`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  return (
    <button
      onClick={open}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-norea-bg border border-norea-border text-xs hover:border-norea-primary/40 transition"
    >
      <FileText className="w-3.5 h-3.5 text-norea-primary" />
      <span className="text-norea-text">{att.filename}</span>
      <span className="text-norea-textMuted">· {(att.size / 1024).toFixed(0)} Ko</span>
    </button>
  );
}

// --- Compose box with @ autocomplete + file attach ---
function ComposeBox({ entityKey, entityId, candidates, myEmail, myName, onSent }) {
  const urlBase = urlBaseForEntityKey(entityKey);
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [sugIdx, setSugIdx] = useState(0);
  const [sugStart, setSugStart] = useState(-1);
  const textareaRef = useRef(null);

  // Voice → Whisper dictation state
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const startRecording = async () => {
    if (recording || transcribing || busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        chunksRef.current = [];
        if (!blob.size) { toast.error("Aucun audio capturé"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/assistant/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (data?.text || "").trim();
          if (!text) { toast.warning("Rien d'audible n'a été détecté"); return; }
          // Insert at the current caret position (don't overwrite existing typing)
          const el = textareaRef.current;
          const start = el?.selectionStart ?? body.length;
          const end = el?.selectionEnd ?? body.length;
          const needSpaceBefore = start > 0 && body[start - 1] && !/\s/.test(body[start - 1]);
          const needSpaceAfter = end < body.length && body[end] && !/\s/.test(body[end]);
          const insert = (needSpaceBefore ? " " : "") + text + (needSpaceAfter ? " " : "");
          const newBody = body.slice(0, start) + insert + body.slice(end);
          setBody(newBody);
          setTimeout(() => {
            const pos = start + insert.length;
            el?.focus();
            el?.setSelectionRange(pos, pos);
          }, 0);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription impossible");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone inaccessible — vérifie les permissions du navigateur");
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
  };

  // Auto-stop recording when component unmounts (defensive)
  useEffect(() => () => { if (recording) stopRecording(); /* eslint-disable-next-line */ }, []);

  // Cleanup preview URL
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const filteredCandidates = useMemo(() => candidates.filter((c) => c.email !== myEmail), [candidates, myEmail]);

  const updateSuggestions = (value, caretPos) => {
    // Find the last "@" before caret that starts a token (preceded by whitespace or beginning)
    const before = value.slice(0, caretPos);
    const m = /(?:^|\s)@([a-zA-Z0-9.\-]*)$/.exec(before);
    if (!m) {
      setSuggestions([]);
      setSugStart(-1);
      return;
    }
    const query = m[1].toLowerCase();
    const start = caretPos - m[1].length - 1; // position of '@'
    const matches = filteredCandidates
      .filter((c) => c.handle.startsWith(query) || c.name.toLowerCase().includes(query))
      .slice(0, 6);
    setSuggestions(matches);
    setSugIdx(0);
    setSugStart(start);
  };

  const onBodyChange = (e) => {
    const v = e.target.value;
    setBody(v);
    updateSuggestions(v, e.target.selectionStart);
  };

  const insertMention = (c) => {
    if (sugStart < 0) return;
    const before = body.slice(0, sugStart);
    const after = body.slice(textareaRef.current?.selectionStart ?? body.length);
    const newVal = `${before}@${c.handle} ${after}`;
    setBody(newVal);
    setSuggestions([]);
    setSugStart(-1);
    // Restore focus + caret
    setTimeout(() => {
      const pos = (before + `@${c.handle} `).length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const onKeyDown = (e) => {
    if (suggestions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSugIdx((i) => (i + 1) % suggestions.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSugIdx((i) => (i - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(suggestions[sugIdx]);
        return;
      }
      if (e.key === "Escape") { setSuggestions([]); setSugStart(-1); return; }
    }
    // Submit on Cmd/Ctrl + Enter
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (10 Mo max)");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
  };

  const removeFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
  };

  const submit = async () => {
    if (!body.trim() && !file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("body", body);
      if (file) form.append("file", file);
      const { data } = await api.post(`${urlBase}/${entityId}/messages`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSent(data.message);
      if (data.mentions_notified > 0) {
        toast.success(`Message envoyé · ${data.mentions_notified} mention(s) notifiée(s) par email`);
      }
      setBody("");
      removeFile();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-norea-border rounded-md p-3 bg-norea-bg/60 relative">
      <div className="flex items-center gap-2 mb-2 text-[11px] text-norea-textMuted">
        <div className="w-6 h-6 rounded-full bg-norea-primary/15 flex items-center justify-center text-[9px] font-semibold text-norea-primary">
          {initials(myName)}
        </div>
        <span>En tant que <strong>{myName}</strong></span>
      </div>

      <textarea
        ref={textareaRef}
        value={body}
        onChange={onBodyChange}
        onKeyDown={onKeyDown}
        rows={3}
        className={`w-full bg-white border rounded p-2 text-sm focus:outline-none resize-y transition ${
          recording
            ? "border-status-erreurBorder ring-1 ring-status-erreurBorder"
            : "border-norea-border focus:border-norea-primary/50"
        }`}
        placeholder={
          recording ? "🔴 Dictée en cours… clique sur ⏹ Arrêter pour insérer le texte"
          : transcribing ? "Transcription en cours…"
          : "Écrivez un message… Utilisez @ pour mentionner un collègue. (Cmd/Ctrl+Entrée pour envoyer)"
        }
        disabled={recording || transcribing}
        data-testid="discussion-textarea"
      />

      {/* Mention autocomplete */}
      {suggestions.length > 0 && (
        <div
          className="absolute left-3 right-3 mt-1 bg-white border border-norea-border rounded shadow-lg z-10 max-h-56 overflow-y-auto"
          data-testid="mention-suggestions"
        >
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={s.email}
              onMouseDown={(e) => { e.preventDefault(); insertMention(s); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${
                i === sugIdx ? "bg-norea-primary/10" : "hover:bg-norea-surfaceHover"
              }`}
              data-testid={`mention-option-${s.handle}`}
            >
              <AtSign className="w-3.5 h-3.5 text-norea-primary" />
              <span className="font-mono text-[12px] text-norea-primary">@{s.handle}</span>
              <span className="text-norea-textMuted text-[11px] ml-auto">{s.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Attachment preview */}
      {file && (
        <div className="mt-2 inline-flex items-center gap-2 bg-white border border-norea-border rounded px-2 py-1.5 text-xs" data-testid="attachment-preview">
          {previewUrl ? <ImageIcon className="w-3.5 h-3.5 text-norea-primary" /> : <FileText className="w-3.5 h-3.5 text-norea-primary" />}
          {previewUrl && <img src={previewUrl} alt="" className="w-8 h-8 object-cover rounded" />}
          <span className="text-norea-text">{file.name}</span>
          <span className="text-norea-textMuted">({(file.size / 1024).toFixed(0)} Ko)</span>
          <button onClick={removeFile} className="ml-1 text-norea-textMuted hover:text-status-erreurText" data-testid="btn-remove-attachment">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1.5 text-xs text-norea-textMuted hover:text-norea-primary cursor-pointer" data-testid="btn-attach-file">
            <Paperclip className="w-3.5 h-3.5" />
            Joindre un fichier
            <input
              type="file"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            />
          </label>
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={busy || transcribing}
            className={`inline-flex items-center gap-1.5 text-xs cursor-pointer transition ${
              recording
                ? "text-status-erreurText animate-pulse"
                : transcribing
                ? "text-norea-textMuted"
                : "text-norea-textMuted hover:text-norea-primary"
            }`}
            data-testid="btn-discussion-mic"
            title={recording ? "Arrêter la dictée" : transcribing ? "Transcription en cours…" : "Dicter à la voix (sera ajouté au message)"}
          >
            {transcribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : recording ? <Square className="w-3.5 h-3.5 fill-current" />
              : <Mic className="w-3.5 h-3.5" />}
            {recording ? "Arrêter" : transcribing ? "Transcription…" : "Dicter"}
          </button>
        </div>
        <button
          onClick={submit}
          disabled={busy || recording || transcribing || (!body.trim() && !file)}
          className="btn-primary text-xs"
          data-testid="btn-send-message"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Envoyer
        </button>
      </div>
    </div>
  );
}
